import {
  ArrowRight,
  Heart,
  Leaf,
  PackageCheck,
  Sparkles
} from "lucide-react";

import {
  Link
} from "react-router";

export function OurStoryPage() {
  return (
    <main>
      {/* ================================================= */}
      {/* HERO */}
      {/* ================================================= */}

      <section className="overflow-hidden bg-[#efe3d2]">
        <div className="rini-container py-20 text-center sm:py-24 lg:py-28">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#6f402d] shadow-sm">
            <Heart
              size={24}
              strokeWidth={1.5}
            />
          </span>

          <p className="mt-7 text-sm font-semibold uppercase tracking-[0.24em] text-[#70845b]">
            The RiNi story
          </p>

          <h1 className="rini-heading mx-auto mt-4 max-w-5xl text-5xl font-semibold leading-tight sm:text-6xl lg:text-7xl">
            Thoughtful bodycare,
            made with care
          </h1>

          <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[#746d65]">
            RiNi Bodycare is built around simple rituals,
            carefully considered ingredients and the idea
            that everyday self-care should feel personal,
            meaningful and beautifully made.
          </p>

          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="rini-button-primary"
            >
              Explore RiNi

              <ArrowRight
                size={18}
              />
            </Link>

            <Link
              to="/making-rini"
              className="rini-button-secondary"
            >
              See how RiNi is made
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* INTRO STORY */}
      {/* ================================================= */}

      <section className="rini-section bg-[#fffdf8]">
        <div className="rini-container">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="rounded-[2.5rem] bg-[#edf1e8] p-10 sm:p-12">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Why RiNi began
              </p>

              <h2 className="rini-heading mt-4 text-4xl font-semibold leading-tight sm:text-5xl">
                Care that feels closer to home
              </h2>
            </div>

            <div>
              <p className="text-lg leading-8 text-[#746d65]">
                RiNi began with a simple idea: everyday
                bodycare can be thoughtfully prepared,
                beautifully presented and made with genuine
                attention to how it feels in daily use.
              </p>

              <p className="mt-6 text-lg leading-8 text-[#746d65]">
                Instead of treating self-care as something
                complicated, RiNi focuses on familiar rituals,
                carefully considered ingredients and products
                that fit naturally into everyday routines.
              </p>

              <p className="mt-6 text-lg leading-8 text-[#746d65]">
                That same philosophy shapes every part of
                the RiNi experience, from formulation and
                small-batch preparation to packaging,
                gifting and the final customer experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* BRAND VALUES */}
      {/* ================================================= */}

      <section className="rini-section bg-[#f5efe7]">
        <div className="rini-container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              What guides RiNi
            </p>

            <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
              Built around thoughtful choices
            </h2>

            <p className="mx-auto mt-5 max-w-2xl leading-8 text-[#746d65]">
              Every product and every customer interaction
              is shaped by a few simple principles.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-[2rem] bg-white p-8">
              <Leaf
                size={28}
                className="text-[#70845b]"
                strokeWidth={1.5}
              />

              <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                Considered ingredients
              </h3>

              <p className="mt-3 leading-7 text-[#746d65]">
                Ingredient choices are made with attention
                to the product experience, texture and
                everyday routine.
              </p>
            </article>

            <article className="rounded-[2rem] bg-white p-8">
              <Sparkles
                size={28}
                className="text-[#a27a4d]"
                strokeWidth={1.5}
              />

              <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                Small-batch care
              </h3>

              <p className="mt-3 leading-7 text-[#746d65]">
                RiNi values controlled preparation and
                attention to consistency at every stage.
              </p>
            </article>

            <article className="rounded-[2rem] bg-white p-8">
              <PackageCheck
                size={28}
                className="text-[#6f5140]"
                strokeWidth={1.5}
              />

              <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                Carefully finished
              </h3>

              <p className="mt-3 leading-7 text-[#746d65]">
                Packaging and presentation are treated as
                part of the complete RiNi experience.
              </p>
            </article>

            <article className="rounded-[2rem] bg-white p-8">
              <Heart
                size={28}
                className="text-[#8b5a49]"
                strokeWidth={1.5}
              />

              <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                Made to feel personal
              </h3>

              <p className="mt-3 leading-7 text-[#746d65]">
                Products, gifting and customer care are
                designed to feel thoughtful rather than
                mass-produced.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* HANDMADE PHILOSOPHY */}
      {/* ================================================= */}

      <section className="rini-section bg-[#edf1e8]">
        <div className="rini-container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Handmade philosophy
              </p>

              <h2 className="rini-heading mt-4 text-4xl font-semibold leading-tight sm:text-5xl">
                Small details make a big difference
              </h2>

              <p className="mt-6 text-lg leading-8 text-[#746d65]">
                RiNi's approach places importance on the
                details that customers experience directly:
                texture, fragrance, presentation, consistency
                and the feeling of receiving something that
                has been carefully prepared.
              </p>

              <p className="mt-5 text-lg leading-8 text-[#746d65]">
                Small-batch preparation allows us to remain
                closer to those details throughout the
                making and finishing process.
              </p>

              <Link
                to="/making-rini"
                className="rini-button-primary mt-8"
              >
                Explore the making process

                <ArrowRight
                  size={18}
                />
              </Link>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-[2rem] bg-white p-8 sm:translate-y-8">
                <p className="font-serif text-5xl text-[#5e3b2c]">
                  01
                </p>

                <h3 className="mt-5 font-serif text-2xl text-[#38251e]">
                  Prepare
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Ingredients and product requirements
                  are thoughtfully prepared.
                </p>
              </div>

              <div className="rounded-[2rem] bg-[#efe3d2] p-8">
                <p className="font-serif text-5xl text-[#5e3b2c]">
                  02
                </p>

                <h3 className="mt-5 font-serif text-2xl text-[#38251e]">
                  Craft
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Products are prepared in controlled,
                  carefully managed batches.
                </p>
              </div>

              <div className="rounded-[2rem] bg-[#f3e7d9] p-8 sm:translate-y-8">
                <p className="font-serif text-5xl text-[#5e3b2c]">
                  03
                </p>

                <h3 className="mt-5 font-serif text-2xl text-[#38251e]">
                  Check
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Texture, consistency and finishing
                  details are carefully reviewed.
                </p>
              </div>

              <div className="rounded-[2rem] bg-white p-8">
                <p className="font-serif text-5xl text-[#5e3b2c]">
                  04
                </p>

                <h3 className="mt-5 font-serif text-2xl text-[#38251e]">
                  Pack
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Every order is thoughtfully prepared
                  for its final journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* INGREDIENT CONNECTION */}
      {/* ================================================= */}

      <section className="rini-section bg-[#fffdf8]">
        <div className="rini-container text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
            <Leaf
              size={24}
              strokeWidth={1.5}
            />
          </span>

          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Naturally considered
          </p>

          <h2 className="rini-heading mx-auto mt-4 max-w-3xl text-4xl font-semibold sm:text-5xl">
            Every RiNi story begins with thoughtful ingredients
          </h2>

          <p className="mx-auto mt-5 max-w-2xl leading-8 text-[#746d65]">
            Discover the botanical ingredients and
            ingredient stories behind RiNi products.
          </p>

          <Link
            to="/ingredients"
            className="rini-button-secondary mt-8"
          >
            Explore ingredients

            <ArrowRight
              size={18}
            />
          </Link>
        </div>
      </section>

      {/* ================================================= */}
      {/* BRAND MESSAGE */}
      {/* ================================================= */}

      <section className="bg-[#38251e] text-white">
        <div className="rini-container py-20 text-center sm:py-24">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#d8c3aa]">
            RiNi Bodycare
          </p>

          <h2 className="mx-auto mt-5 max-w-4xl font-serif text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            Self-care should feel thoughtful,
            personal and beautifully simple.
          </h2>

          <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-white/70">
            That idea continues to guide every product,
            every hamper and every RiNi experience.
          </p>
        </div>
      </section>

      {/* ================================================= */}
      {/* FINAL CTA */}
      {/* ================================================= */}

      <section className="bg-[#efe3d2]">
        <div className="rini-container py-16 text-center sm:py-20">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Discover RiNi
          </p>

          <h2 className="rini-heading mx-auto mt-4 max-w-3xl text-4xl font-semibold sm:text-5xl">
            Find your everyday RiNi ritual
          </h2>

          <p className="mx-auto mt-5 max-w-2xl leading-8 text-[#746d65]">
            Explore bodycare, haircare, lip care and
            thoughtfully curated gifting collections.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="rini-button-primary"
            >
              Shop the collection

              <ArrowRight
                size={18}
              />
            </Link>

            <Link
              to="/gift-hampers"
              className="rini-button-secondary"
            >
              Explore gift hampers
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
